import React from 'react'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  Alert,
  AlertTitle,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  type StyleProps,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import * as exportAPI from './export-api'
import { useExportJobs } from './export-items-state'
import { ExportJobBasic } from './export-queries'
import {
  commonExportStyles,
  ExportHeader,
  getProgress,
  getStatusLabel,
} from './export-result-common'
import { getErrorMessage } from './export-utils'

const styles = {
  tabTitle: {
    typography: 'h5',
    mt: 2,
    mb: 4,
    px: 3,
  },
  ...commonExportStyles,
  table: {
    mt: 2,
    '& td, & th': {
      py: 1,
    },
    '& tbody td': {
      borderBottom: 0,
    },
    '& tbody tr:last-of-type td': {
      borderBottom: '1px solid',
      borderColor: 'divider',
    },
    '& tbody tr:nth-of-type(odd)': {
      bgcolor: 'background.grey',
    },
  },
} satisfies StyleProps

const getDeliveryStatusLabel = (status: string) => {
  const labelMap = {
    success: 'Success',
    failed: 'Failed',
    in_progress: 'In Progress',
    pending: 'Pending',
    retrying: 'Retrying',
  }

  return labelMap[status] || status
}

const getDeliveryStatusColor = (status: string) => {
  const colorMap = {
    success: 'success',
    failed: 'error',
    in_progress: 'info',
    pending: 'warning',
    retrying: 'warning',
  }

  return colorMap[status] || 'default'
}

type WebhookExportJobRowProps = {
  jobBasic: ExportJobBasic
  isExpanded: boolean
}

const WebhookExportJobRow = ({ jobBasic, isExpanded }: WebhookExportJobRowProps) => {
  const { t } = useTranslation('legacy', { keyPrefix: 'export' })
  const [updateDialog, isChecked] = useExportJobs((state) => [
    state.updateDialog,
    state.exportDialog.selectedJobIds.has(jobBasic.id),
  ])

  const exportDetails = exportAPI.useGetExportDetails(jobBasic.id, isExpanded)

  const webhookDeliveries = exportAPI.useListWebhookDeliveries(jobBasic.id, isExpanded)

  const exportJob = exportDetails.data?.exportJob
  const deliveries = webhookDeliveries.data || []

  const status = exportJob?.status || jobBasic.status
  const isFailed = status === 'failed'
  const isCompleted = status === 'completed'
  const isProcessing = status === 'in_progress' || status === 'pending'
  const hasDeliveryAttempts = deliveries.length > 0

  const showContent = Boolean(isExpanded && exportDetails.data && !exportDetails.isLoading)
  const showError = Boolean(
    isExpanded && isFailed && !hasDeliveryAttempts && !exportDetails.isLoading,
  )
  const exportLabel = jobBasic.webhookDeliveryAttempts?.[0]?.webhookConfig?.label
  const handleCheck = () => {
    if (isChecked) {
      updateDialog((state) => {
        state.selectedJobIds.delete(jobBasic.id)
      })
    } else {
      updateDialog((state) => {
        state.selectedJobIds.add(jobBasic.id)
      })
    }
  }

  const progress = getProgress(exportJob)

  return (
    <Box my={2} bgcolor="background.grey" p={2} mx={3} borderRadius={2}>
      <Box aria-label="export-job-header" sx={styles.exportJobHeader}>
        <Checkbox onChange={handleCheck} checked={isChecked} color="secondary" size="small" />
        <ExportHeader job={jobBasic} showBadge={isFailed} badgeColor="error" />
        <IconButton
          disabled={exportDetails.isLoading}
          onClick={() =>
            updateDialog({
              selectedJobId: isExpanded ? '' : jobBasic.id,
            })
          }
        >
          {isExpanded ? (
            exportDetails.isLoading ? (
              <CircularProgress size={20} color="secondary" />
            ) : (
              <ExpandLess />
            )
          ) : (
            <ExpandMore />
          )}
        </IconButton>
      </Box>

      <Collapse in={showError} timeout="auto" unmountOnExit>
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>{getErrorMessage(exportJob?.errorDetails).title}</AlertTitle>
          {getErrorMessage(exportJob?.errorDetails).message}
        </Alert>
      </Collapse>

      <Collapse in={showContent} timeout="auto" unmountOnExit>
        <Box mt={2}>
          {isProcessing && (
            <Box mb={3}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                typography="h5"
                sx={{ mb: 1 }}
              >
                <span>{getStatusLabel(status)}</span>
                <span>{progress.toFixed(1)}%</span>
              </Box>
              <LinearProgress
                value={progress}
                variant={exportJob?.totalItems ? 'determinate' : 'indeterminate'}
                color="secondary"
              />
              {!!exportJob?.totalItems && (
                <Box typography="h6" mt={1}>
                  {exportJob.totalItems === 1 ? '1 item' : `${exportJob.totalItems} items`}
                </Box>
              )}
            </Box>
          )}

          {/* Webhook Export Status */}
          {(isCompleted || (isFailed && hasDeliveryAttempts)) && (
            <Box>
              <Typography variant="h6" mb={2}>
                {t('webhook_status')}
              </Typography>

              {webhookDeliveries.isLoading && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Loading export status...</Typography>
                </Box>
              )}

              {!webhookDeliveries.isLoading && deliveries.length === 0 && (
                <Alert severity="info">{t('no_attempts_found')}</Alert>
              )}

              {!webhookDeliveries.isLoading && !isFailed && deliveries.length > 0 && (
                <Alert severity="success">{t('request_received_by', { label: exportLabel })}</Alert>
              )}

              {!webhookDeliveries.isLoading && isFailed && deliveries.length > 0 && (
                <Table sx={styles.table} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('attempt')}</TableCell>
                      <TableCell>{t('status')}</TableCell>
                      <TableCell>HTTP</TableCell>
                      <TableCell>{t('error')}</TableCell>
                      <TableCell>{t('attempted')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deliveries.map((delivery) => (
                      <TableRow key={delivery.id}>
                        <TableCell>#{delivery.attemptNumber}</TableCell>
                        <TableCell>
                          <Chip
                            label={getDeliveryStatusLabel(delivery.status)}
                            color={getDeliveryStatusColor(delivery.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{delivery.httpStatusCode || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 300 }}>
                          <Typography variant="body2" noWrap>
                            {delivery.errorMessage || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {new Date(delivery.attemptedAt).toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {!!exportJob?.totalItems && (
                <Box typography="h6" mt={2}>
                  {exportJob.totalItems === 1
                    ? '1 item exported'
                    : `${exportJob.totalItems} items exported`}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

export default WebhookExportJobRow
